import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { renderAcademicMarkdown } from '../lib/markdown';
import { safeText } from '../lib/render';

function LivePreview({ text }) {
  const { html, references } = useMemo(() => renderAcademicMarkdown(text), [text]);
  return (
    <>
      <article
        className="dissertation-article"
        style={{ lineHeight: 1.8, fontSize: '1rem' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {references.length > 0 && (
        <>
          <hr className="my-3" />
          <div className="small text-muted mb-2">
            References so far ({references.length})
          </div>
          <ol className="small" style={{ lineHeight: 1.6, paddingLeft: '1.2rem' }}>
            {references.map((r, i) => (
              <li key={i} style={{ marginBottom: '0.25rem', wordBreak: 'break-word' }}>
                <a href={r.url} target="_blank" rel="noreferrer">{r.url}</a>
              </li>
            ))}
          </ol>
        </>
      )}
    </>
  );
}

export default function AssignmentLive() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const stopped = useRef(false);

  useEffect(() => {
    if (!id) return;
    stopped.current = false;
    let timer = null;

    async function poll() {
      if (stopped.current) return;
      try {
        const res = await api.get(`/api/assignment/${id}/live-progress`);
        setData(res);
        setErr(null);
        if (res.status === 'completed') {
          stopped.current = true;
          setTimeout(() => nav(`/assignment/${id}`), 1500);
          return;
        }
        if (res.status === 'failed' || res.error) {
          stopped.current = true;
          return;
        }
      } catch (e) {
        // Stop on terminal errors — don't hammer the server for an assignment
        // that was deleted (404), is not ours (403), or an unauthenticated
        // session (401). Transient 5xx / network errors keep polling.
        if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
          stopped.current = true;
          setErr(e.status === 404 ? 'This assignment no longer exists — it may have been deleted.' : e.message);
          return;
        }
        setErr(e instanceof Error ? e.message : 'Poll failed');
      }
      timer = setTimeout(poll, 3000);
    }

    void poll();
    return () => {
      stopped.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, nav]);

  const percent = Math.round(data?.live_progress?.percent ?? 0);
  const status = (data?.status || 'pending').toLowerCase();
  const failed = status === 'failed' || !!data?.error;
  const completed = status === 'completed';
  const words = data?.live_progress?.word_count_so_far ?? 0;
  const target = data?.word_count_target ?? 0;
  const running = !completed && !failed;
  const elapsedSeconds = useAssignmentElapsed(data?.created_at, running);
  const phaseLabel = safeText(data?.live_progress?.phase ?? status);
  const progressMessage =
    safeText(data?.live_progress?.message, '') ||
    (words > 0
      ? `Writing your assignment — ${words.toLocaleString()} words drafted`
      : completed
      ? 'Assignment ready'
      : failed
      ? 'Generation failed'
      : 'Starting up…');

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'rgba(0,0,0,0.06)',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: failed
              ? '#ef4444'
              : 'linear-gradient(90deg, var(--primary), var(--secondary))',
            transition: 'width 0.5s ease',
          }}
        />
      </div>

      <div className="main-container">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <Link to="/assignments" className="text-muted small">
              <i className="fas fa-arrow-left me-1"></i>Back to assignments
            </Link>

            <div className="d-flex align-items-center justify-content-between mt-2 mb-4 flex-wrap gap-2">
              <div>
                <h1 className="mb-1" style={{ fontWeight: 700 }}>
                  {completed ? 'Assignment ready' : failed ? 'Generation failed' : 'Generating assignment…'}
                </h1>
                <p className="text-muted mb-0">
                  {data?.topic || 'Preparing your document'}
                  {data?.assignment_type && <> • {data.assignment_type}</>}
                </p>
              </div>
              <span className={`status-badge ${status}`}>{status}</span>
            </div>

            {err && <div className="alert alert-warning">{err}</div>}
            {failed && <div className="alert alert-danger">{data?.error || 'Generation failed.'}</div>}

            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-3 gap-2">
                  <div>
                    <div
                      className="text-muted small text-uppercase"
                      style={{ letterSpacing: 2, fontSize: 11 }}
                    >
                      {phaseLabel}
                    </div>
                    <div className="fw-semibold mt-1">
                      {progressMessage}
                    </div>
                    <div className="text-muted small mt-1">
                      {words.toLocaleString()} / {target.toLocaleString()} words
                      <span className="mx-2">·</span>
                      <i className="fas fa-stopwatch me-1"></i>
                      {completed ? 'Total' : 'Elapsed'}: {formatAssignmentElapsed(elapsedSeconds)}
                    </div>
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--primary)' }}>
                    {percent}%
                  </div>
                </div>
                <div className="progress" style={{ height: 10 }}>
                  <div
                    className={`progress-bar${completed ? '' : ' progress-bar-striped progress-bar-animated'}`}
                    role="progressbar"
                    style={{
                      width: `${percent}%`,
                      background: failed
                        ? '#ef4444'
                        : 'linear-gradient(90deg, var(--primary), var(--secondary))',
                    }}
                  />
                </div>
              </div>
            </div>

            {data?.content_preview && (
              <div className="card">
                <div className="card-body">
                  <div
                    className="text-muted small text-uppercase mb-3"
                    style={{ letterSpacing: 2, fontSize: 11 }}
                  >
                    Preview
                  </div>
                  <LivePreview text={data.content_preview} />
                </div>
              </div>
            )}

            {completed && (
              <div className="text-center mt-4">
                <Link to={`/assignment/${id}`} className="btn btn-cta btn-lg">
                  <i className="fas fa-file-alt me-1"></i>Open your assignment
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function useAssignmentElapsed(startIso, running) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    const onVis = () => setTick((v) => v + 1);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [running]);
  if (!startIso) return 0;
  const start = Date.parse(startIso);
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}

function formatAssignmentElapsed(totalSeconds) {
  if (!totalSeconds || totalSeconds < 0) return '0s';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
